import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  useClientMembers,
  useCurrentClient,
} from "@/features/clients/hooks/use-clients";
import { useHasPermission } from "@/features/clients/hooks/use-permissions";
import {
  useAssignMessage,
  useHandleMessage,
} from "@/features/messages/hooks/use-messages";
import type { ClientMessage } from "@/features/messages/types";
import { useTranslation } from "@/features/utils/i18n";
import { DotsThreeOutlineIcon } from "@phosphor-icons/react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";

/**
 * Everything one can do to a request besides answering it: open the call it came
 * from, hand it to somebody, or drop it. Shared by a table row and the reading pane.
 */
export const MessageMenu = ({ message }: { message: ClientMessage }) => {
  const { t } = useTranslation("messages");
  const { company: routeClientId } = useParams<{ company: string }>();
  const clientId = useCurrentClient()?.client?.id || "";
  const canManage = useHasPermission("messages:manage");
  const { data: members = [] } = useClientMembers(clientId);
  const assignMessage = useAssignMessage();
  const handleMessage = useHandleMessage();

  const assign = async (memberId: string, memberName = "") => {
    try {
      await assignMessage.mutateAsync({ id: message.id, memberId, memberName });
      toast.success(t("assign.done"));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("assign.failed"));
    }
  };

  // "Supprimer" closes the request as `dropped` — the row stays in "Traitées",
  // since a request the customer made is part of their history.
  const remove = async () => {
    try {
      await handleMessage.mutateAsync({
        id: message.id,
        resolution: "dropped",
      });
      toast.success(t("actions.deleted"));
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("actions.deleteFailed"),
      );
    }
  };

  const namedMembers = members.filter((m) =>
    (m.membership.display_name || m.user?.full_name)?.trim(),
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={t("actions.more")}>
          <DotsThreeOutlineIcon className="h-4 w-4" weight="fill" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {message.session_id && (
          <DropdownMenuItem asChild>
            <Link to={`/${routeClientId}/calls/session/${message.session_id}`}>
              {t("actions.openConversation")}
            </Link>
          </DropdownMenuItem>
        )}

        {canManage && message.status === "pending" && (
          <>
            <DropdownMenuSeparator />
            {/* Assignment is a filter, not a routing: everyone still sees the
                  request. Nothing notifies the person named here — that is the
                  notifications package, deliberately separate. */}
            <DropdownMenuLabel>{t("assign.label")}</DropdownMenuLabel>
            {message.assigned_member_id && (
              <DropdownMenuItem onClick={() => assign("")}>
                {t("assign.clear")}
              </DropdownMenuItem>
            )}
            {namedMembers.map((member) => (
              <DropdownMenuItem
                key={member.membership.id}
                onClick={() =>
                  assign(
                    member.membership.id,
                    member.membership.display_name ||
                      member.user?.full_name ||
                      "",
                  )
                }
                disabled={member.membership.id === message.assigned_member_id}
              >
                {member.membership.display_name || member.user?.full_name}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={remove}
              disabled={handleMessage.isPending}
            >
              {t("actions.delete")}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
